<!-- eslint-disable vue/no-confusing-v-for-v-if -->
<template>
  <main class="main main--has-floating-buttons">
    <spinner-overlay v-if="createReservationInProgress" />
    <hm-col :grid="true" class="grid--row-gap" cols="12 6@tablet 8@desktop 9@hd" rows="3">
      <template v-for="(offer, offerIdx) in selectedOffers">
        <card :title="$t('pageDraftReservation.card.reservation', { idx: offerIdx+1 })" :key="offer.offerId" cols="12">
          <hm-card-inner :title="$t('pageDraftReservation.card.reservationDetails')">
            <hm-input-date v-model="offer.sArrivalDate"
                           :disabled="true"
                           :label="$t('pageDraftReservation.arrivalDate')"
                           cols="12 6@tablet 3@desktop"
                           name="arrivalDate" />
            <hm-input-date v-model="offer.sDepartureDate"
                           :min-date="offer.sArrivalDate"
                           :disabled="true"
                           :label="$t('pageDraftReservation.departureDate')"
                           cols="12 6@tablet 3@desktop"
                           name="departureDate" />
            <hm-input-select v-model="offer.roomTypeId"
                             :disabled="true"
                             :label="$t('pageDraftReservation.roomType')"
                             cols="12 6@tablet 3@desktop"
                             name="roomType">
              <option v-for="item in hotelSettings.roomTypes.all(offer.roomTypeId)" :value="item.id" v-html="item.name[$shortLocale]"></option>
            </hm-input-select>
            <hm-input-select v-model="offer.ratePlanId"
                             :disabled="true"
                             :label="$t('pageDraftReservation.rate')"
                             cols="12 6@tablet 3@desktop"
                             name="rate">
              <option v-for="item in hotelSettings.ratePlans.all(offer.ratePlanId)" :value="item.id" v-html="item.name[$shortLocale]"></option>
            </hm-input-select>
            <hm-input v-model="offer.roomCount"
                      :disabled="true"
                      :label="$t('pageDraftReservation.selected')"
                      cols="12 6@tablet 3@desktop"
                      name="selected" />
            <hm-input v-if="!isEmpty(offer.roomNumber)"
                      v-model="offer.roomNumber"
                      :disabled="true"
                      :label="$t('pageDraftReservation.roomNumber')"
                      cols="12 6@tablet 3@desktop"
                      name="roomNumber" />
            <hm-input-select v-model="offer.numAdults"
                             :disabled="true"
                             :label="$t('pageDraftReservation.numAdults')"
                             name="numAdults"
                             cols="12 6@tablet 3@desktop">
              <option v-for="item in intArray(100)" :value="item" :disabled="item > (99 - offer.numChildren)" v-html="item"></option>
            </hm-input-select>
            <hm-input-select v-if="hasLicense('ChildRates')"
                             v-model="offer.numChildren"
                             :disabled="true"
                             :label="$t('pageDraftReservation.numChildren')"
                             name="numChildren"
                             cols="12 6@tablet 3@desktop"
                             @input="onUpdateNumChildren(offer)"
                             @change="onUpdateNumChildren(offer)">
              <option v-for="item in intArray(100)" :value="item" :disabled="item > (99 - offer.numAdults)" v-html="item"></option>
            </hm-input-select>
            <hm-input-select v-for="child in intArray(offer.numChildren)"
                             v-model="offer.childAgeCategories[child+1]"
                             :disabled="true"
                             :label="$tc('pageDraftReservation.child', child, { count: child+1 })"
                             :key="child"
                             :required="true"
                             name="childAge"
                             cols="12 6@tablet 3@desktop">
              <option v-for="item in hotelSettings.ageCategories.all(offer.childAgeCategories[child+1])" v-if="item.ageQualifier === 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]}`"></option>
              <option v-for="item in hotelSettings.ageCategories.all(offer.childAgeCategories[child+1])" v-if="item.ageQualifier !== 'Adult'" :value="item.id" v-html="`${item.name[$shortLocale]} (${item.ageFrom} - ${item.ageUntil})`"></option>
            </hm-input-select>
          </hm-card-inner>
          <hm-card-inner :title="$t('pageDraftReservation.card.guest')">
            <line-profile-editor v-model="offer.guestProfileId" :label="$t('pageDraftReservation.guestProfileId')" :help="$t('pageDraftReservation.guestProfileId_help')" type="Guest" />
          </hm-card-inner>
          <hm-card-inner v-if="!isNil(offer.discount)" :title="$t('pageDraftReservation.card.discount')">
            <hm-input-money v-model="offer.discount.percentage"
                            :disabled="false"
                            :label="$t('pageDraftReservation.discount')"
                            :help="$t('pageDraftReservation.discount_help')"
                            name="discount"
                            currency="%"
                            validate="max_value:100|min_value:0"
                            cols="12 6@tablet 3@desktop"
                            @input="debouncedUpdateRates">
            </hm-input-money>
            <hm-input-select v-if="hasLicense('DiscountCode')"
                             v-model="offer.discount.discountCode"
                             :label="$t('pageDraftReservation.discountCode')"
                             cols="12 6@tablet 3@desktop"
                             @input="debouncedUpdateRates">
              <option :value="null"></option>
              <template v-for="it in (hotelSettings.discountCodes || [])">
                <option :key="it.code" :value="it.code">{{ it.code }}</option>
              </template>
            </hm-input-select>
          </hm-card-inner>
          <hm-card-inner :full-size="true">
            <rate-editor v-if="!isEmpty(offer.rates)"
                         ref="rateEditor"
                         :rates.sync="offerRates[offerIdx]"
                         :arrival-date="offer.sArrivalDate"
                         :departure-date="offer.sDepartureDate"
                         :num-nights="offer.numNights"
                         :locked="rateUpdateInProgress"
                         :updating="rateUpdateInProgress"
                         :discount="offer.discount"
                         @update:rates="onUpdateRates($event, offerIdx)"
                         @ratechange="debouncedUpdateRates" />
          </hm-card-inner>
          <hm-card-inner>
            <hm-button v-if="hasLicense('AdditionalServices')"
                       class="btn--blue btn--no-margin"
                       cols="12 6@tablet 3@desktop 2@hd start-1 start-7@tablet start-10@desktop start-11@hd"
                       icon-left="plus"
                       @click="showAddTransactionModal = offer.offerId">{{ $t('pageDraftReservation.actions.addAdditionalService') }}</hm-button>
          </hm-card-inner>
        </card>
      </template>
    </hm-col>
    <tabable-card id="guests" cols="12 6@tablet 4@desktop 3@hd" rows="1">
      <tabable-card-content id="guest" :title="$t('pageDraftReservation.card.guestProfile.guest')" :indicator="isEmpty(reservationRequest.bookerId) ? null : 'green'">
        <guest-profile-editor id="editor_guest" v-model="reservationRequest.bookerId" type="Guest" />
      </tabable-card-content>
      <tabable-card-content id="company" :title="$t('pageDraftReservation.card.guestProfile.company')" :indicator="isEmpty(reservationRequest.companyProfileId) ? null : 'green'">
        <guest-profile-editor id="editor_company" v-model="reservationRequest.companyProfileId" type="Company" />
      </tabable-card-content>
      <tabable-card-content id="travelAgent" :title="$t('pageDraftReservation.card.guestProfile.travelAgent')" :indicator="isEmpty(reservationRequest.travelAgentProfileId) ? null : 'green'">
        <guest-profile-editor id="editor_travelagent" v-model="reservationRequest.travelAgentProfileId" type="TravelAgent" />
      </tabable-card-content>
    </tabable-card>

    <modal v-if="showAddTransactionModal !== false"
           :close-on-click-outside="false"
           :hide-on-submit="false"
           :title="$t('pageDraftReservation.card.additionalServices.title')"
           :confirm-label="$t('pageDraftReservation.card.additionalServices.buttonAdd')"
           @confirm="addShoppingCartItem()"
           @cancel="showAddTransactionModal=false">
      <hm-card-inner min-height="35.8rem">
        <hm-col :grid="true" cols="12">
          <hm-input-select v-model="newShoppingCartItem.inHouseServiceId" :label="$t('pageDraftReservation.transactionCode')" name="transactionCode" @input="onChangeShoppingCartItemId">
            <option :value="null" :disabled="true"></option>
            <optgroup v-for="group in additionalServices" :label="group.group">
              <option v-for="item in group.options" v-if="!isNil(item.amount)" :value="item.id" v-html="item.name"></option>
            </optgroup>
          </hm-input-select>
          <hm-input-number v-model="newShoppingCartItem.itemCount" :label="$t('pageDraftReservation.itemCount')" name="itemCount" cols="6" />
          <hm-input-money v-model="newShoppingCartItem.itemPrice"
                          :currency="hotelSettings.details.currency"
                          :label="$t('pageDraftReservation.itemPrice')"
                          name="itemPrice"
                          cols="6" />
          <hm-input v-model="newShoppingCartItem.alternativeLabel" :label="$t('pageRoomReservation.itemAlternativeLabel')" name="alternativeLabel" cols="6" />
          <hm-input-date v-if="newShoppingCartItem.date != null"
                         v-model="newShoppingCartItem.date"
                         :max-date="maxCheckOutDate"
                         :min-date="minCheckInDate"
                         :min-date-inclusive="true"
                         :label="$t('pageRoomReservation.itemDate')"
                         name="itemDate"
                         cols="6" />
        </hm-col>
      </hm-card-inner>
    </modal>

    <hm-floating-buttons>
      <hm-floating-button :disabled="!canCreateReservation"
                          :throttle="3500"
                          :label="$t('pageDraftReservation.actions.createReservation')"
                          icon="save"
                          color="green"
                          @click="createReservation(false, false)" />
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { groupBy, uniqBy, isNil, isArray, isEmpty, get, head, debounce, cloneDeep, sortBy, min, max } from 'lodash';
import uuid from 'uuid';
import Icon from '../lib/util/Icon';
import HmCardInner from '../lib/layout/HmCardInner';
import HmCol from '../lib/layout/HmCol';
import LineProfileEditor from '../lib/inputgroups/LineGuestProfileEditor';

export default {
  name: 'PageDraftReservation',
  components: { LineProfileEditor, HmCol, HmCardInner, Icon },
  mixins: [HotelmeisterMixin],


  data() {
    return {
      showAddTransactionModal: false,
      newShoppingCartItem: {
        id: uuid.v4(),
        inHouseServiceId: null,
        itemCount: 1,
        itemPrice: 0,
        alternativeLabel: null,
        date: null,
      },
      rateUpdateInProgress: false,
      reservationRequest: {
        bookerId: null,
        companyProfileId: null,
        travelAgentProfileId: null,
      },
      selectedOffers: [],
      discountCode: null,
      debouncedUpdateRates: null,
      createReservationInProgress: false,
      confirmationNumber: null,
    };
  },

  computed: {
    minCheckInDate: function () {
      if (isEmpty(this.selectedOffers)) return this.$moment().format('YYYYMMDD');
      const offer = this.selectedOffers.find(it => this.showAddTransactionModal === it.offerId);
      if (!isNil(offer)) return offer.sArrivalDate;
      return min(this.selectedOffers.map(it => it.sArrivalDate));
    },
    maxCheckOutDate: function () {
      if (isEmpty(this.offer)) return this.$moment().format('YYYYMMDD');
      const offer = this.selectedOffers.find(it => this.showAddTransactionModal === it.offerId);
      if (!isNil(offer)) return offer.sDepartureDate;
      return max(this.selectedOffers.map(it => it.sDepartureDate));
    },
    offerRates: {
      get: function () {
        return this.selectedOffers.map(it => ({
          rates: cloneDeep(it.rates),
          unitPriceSummary: cloneDeep(it.unitPriceSummary),
          shoppingCart: cloneDeep(it.shoppingCart),
          shoppingCartSummary: cloneDeep(it.shoppingCartSummary),
        }));
      },
      set: function (newValue) {
        if (!isNil(newValue)) {
          newValue.forEach((it, idx) => {
            this.selectedOffers[idx].rates = it.rates;
            this.selectedOffers[idx].unitPriceSummary = it.unitPriceSummary;
            this.selectedOffers[idx].shoppingCart = it.shoppingCart;
            this.selectedOffers[idx].shoppingCartSummary = it.shoppingCartSummary;
          });
        }
      },
    },
    canCreateReservation: function () {
      return !this.rateUpdateInProgress && !this.createReservationInProgress && !isEmpty(this.selectedOffers);
    },
    additionalServices: function () {
      return [
        ...sortBy(
          Object.entries(groupBy(this.hotelSettings.inHouseServices.all(), it => it.type))
            .map(it => ({
              group: this.$t(`InHouseServiceType.${it[0]}`),
              options: sortBy(it[1].map((ihs) => {
                const ret = {
                  id: ihs.id,
                  name: ihs.name[this.$shortLocale],
                  type: 'InHouseService',
                  amount: ihs.amount,
                };

                if (ihs.rateFrequency === 'PerNight') {
                  ret.name = `${ret.name} (${this.$t('RateFrequency.PerNight')})`;
                }

                return ret;
              }), ihs => ihs.name),
            })),
          it => it.group,
        ),
      ];
    },
  },

  watch: {
    discountCode: function (newVal) {
      this.selectedOffers.forEach((it) => {
        if (isNil(it.discount)) it.discount = { percentage: null, discountCode: null };
        it.discount.discountCode = newVal;
      });
    },
  },

  created: function () {
    this.debouncedUpdateRates = debounce(this.updateRates, 1000);

    if (!isNil(this.$route.params.selectedOffers)) {
      this.selectedOffers = cloneDeep(this.$route.params.selectedOffers);
    }

    if (!isEmpty(this.$route.params.discountCode)) {
      this.discountCode = this.$route.params.discountCode;
    }

    if (isEmpty(this.selectedOffers)) {
      window.history.back();
    }

    if (!isNil(this.$route.params.confirmationNumber)) {
      this.confirmationNumber = this.$route.params.confirmationNumber;
    }

    this.selectedOffers.forEach((it) => {
      if (isNil(it.discount) || isNil(it.discount.percentage)) {
        it.discount = { percentage: 0.0 };
      }
      if (isNil(it.numNights)) {
        it.numNights = Math.max(1, this.$moment(it.sArrivalDate, 'YYYYMMDD').diff(this.$moment(it.sDepartureDate, 'YYYYMMDD'), 'days'));
      }
      if (isNil(it.shoppingCart)) {
        it.shoppingCart = [];
      }
      if (isNil(it.shoppingCartSummary)) {
        it.shoppingCartSummary = {
          includedTaxAmount: 0.0,
          amount: 0.0,
          excludedTaxAmount: 0.0,
          currency: this.hotelSettings.details.currency,
          taxDetails: [],
          customAmount: 0.0,
          originalAmount: 0.0,
        };
      }

      it.roomCount = it.selected;
      it.arrivalDate = it.sArrivalDate;
      it.departureDate = it.sDepartureDate;
    });
  },


  beforeRouteLeave(to, from, next) {
    let hasGuestData = false;
    hasGuestData = hasGuestData || !isEmpty(this.reservationRequest.bookerId);
    hasGuestData = hasGuestData || !isEmpty(this.reservationRequest.companyProfileId);
    hasGuestData = hasGuestData || !isEmpty(this.reservationRequest.travelAgentProfileId);
    hasGuestData = hasGuestData || this.selectedOffers.some(offer => !isEmpty(offer.guestProfileId));

    if (!isNil(to) && to.name === 'reservationGroup') {
      next();
    } else if (hasGuestData) {
      this.showConfirmModal(this.$t('pageDraftReservation.messageConfirmLeavePage'), next);
    } else {
      next();
    }
  },

  methods: {
    onUpdateRates($event, offerIdx) {
      this.selectedOffers[offerIdx].rates = $event.rates;
      this.selectedOffers[offerIdx].unitPriceSummary = $event.unitPriceSummary;
      this.selectedOffers[offerIdx].shoppingCart = $event.shoppingCart;
      this.selectedOffers[offerIdx].shoppingCartSummary = $event.shoppingCartSummary;
    },
    onUpdateNumChildren: function (offer) {
      const children = this.intArray(offer.numChildren, 1);
      const defaultAgeCategoryId = isNil(this.hotelSettings.ageCategories.all().find(it => it.ageQualifier !== 'Adult'))
        ? this.hotelSettings.ageCategories.all().find(it => it.ageQualifier === 'Adult').id
        : this.hotelSettings.ageCategories.all().find(it => it.ageQualifier !== 'Adult').id;
      children.forEach((it) => {
        if (isEmpty(get(offer.childAgeCategories, it, null))) {
          offer.childAgeCategories[it] = defaultAgeCategoryId;
        }
      });
    },
    doAllRoomReservationHaveNames: function () {
      if (!isEmpty(this.reservationRequest.bookerId)) return true;
      if (!isEmpty(this.reservationRequest.companyProfileId)) return true;
      if (!isEmpty(this.reservationRequest.travelAgentProfileId)) return true;
      if (this.selectedOffers.filter(it => !isEmpty(it.guestProfileId)).length === this.selectedOffers.length) return true;

      return false;
    },
    createReservation: function (ignoreEmptyNames = false, notification = false) {
      const reservationRequest = {
        hotelId: this.hotelSettings.hotel.id,
        arrivalDate: null,
        departureDate: null,
        confirmationNumber: this.confirmationNumber,
        roomReservationRequests: [
          ...this.selectedOffers.map(it => ({
            roomRequestId: uuid.v4(),
            reservationType: 'Regular',
            arrivalDate: it.sArrivalDate,
            departureDate: it.sDepartureDate,
            numAdults: it.numAdults,
            numChildren: it.numChildren,
            childAgeCategories: it.childAgeCategories,
            roomCount: it.roomCount,
            roomTypeId: it.roomTypeId,
            roomNumber: it.roomNumber,
            ratePlanId: it.ratePlanId,
            rates: it.rates,
            unitPriceSummary: it.unitPriceSummary,
            guestComment: it.guestComment,
            internalComment: it.internalComment,
            discount: it.discount,
            shoppingCart: it.shoppingCart,
            signature: it.signature,
            files: it.files,
            policies: it.policies,
            guestDetails: isEmpty(it.guestProfileId) ? [] : [
              {
                primaryGuest: true,
                guestProfileId: it.guestProfileId,
              },
            ],
          })),
        ],
        booker: {
          primaryGuest: true,
          guestProfileId: this.reservationRequest.bookerId || this.reservationRequest.companyProfileId || this.reservationRequest.travelAgentProfileId,
        },
        companyDetail: {
          guestProfileId: this.reservationRequest.companyProfileId,
        },
        travelAgentDetail: {
          guestProfileId: this.reservationRequest.travelAgentProfileId,
        },
        autoAssignRoomNumber: true,
        additionalFields: {},
        managedBy: 'Hotelmeister',
        version: 1,
      };

      let url = this.serviceUrls.newReservation.request(this);
      if (notification === true) {
        url = this.serviceUrls.newReservation.notification(this);
      }

      if (!ignoreEmptyNames && !this.doAllRoomReservationHaveNames()) {
        this.showModal(
          this.$t('reservation.create.emptyNames'),
          () => { this.createReservation(true, notification); },
        );
      } else {
        if (this.createReservationInProgress === true) return;

        this.createReservationInProgress = true;

        this.axios.post(url, reservationRequest)
          .then((response) => {
            if (response.data.data.status === 'Failed') {
              this.showErrorMessage();
              if (response.data.data.errors.length > 0) {
                this.showModal(
                  this.$t(`MessageWarning.${response.data.data.errors[0].name}`),
                  () => {
                    this.createReservation(true, true);
                  },
                );
              }
            } else {
              this.showMessages(response);
              this.showSuccessMessage();
              this.$router.push({
                name: 'reservationGroup',
                params: { confirmationNumberOrReservationGroupId: response.data.data.reservationGroupId },
              });
            }
          })
          .catch((error) => {
            this.showMessages(error.response);
          })
          .finally(() => {
            this.createReservationInProgress = false;
          });
      }
    },
    updateRates: function () {
      if (!isNil(this.$refs.rateEditor)) {
        let rateEditors = this.$refs.rateEditor;
        if (!isArray(this.$refs.rateEditor)) {
          rateEditors = [rateEditors];
        }

        const notFocusedCount = rateEditors
          .map(it => isNil(it.$el.querySelector(':focus-within')))
          .filter(it => it)
          .length;

        if (notFocusedCount !== rateEditors.length) {
          this.debouncedUpdateRates();
          return;
        }
      }
      this.rateUpdateInProgress = true;
      Promise.all(this.selectedOffers.map(offer => this.axios.post(this.serviceUrls.newReservation.rates(this), offer)))
        .then((results) => {
          this.selectedOffers = results.map(it => it.data.data);
        })
        .finally(() => { this.rateUpdateInProgress = false; });
    },
    onChangeShoppingCartItemId: function () {
      const obj = isEmpty(this.newShoppingCartItem.inHouseServiceId) ? null : this.findHotelSettingById('inHouseServices', this.newShoppingCartItem.inHouseServiceId);
      if (isEmpty(obj)) {
        this.newShoppingCartItem.itemPrice = 0.0;
        this.newShoppingCartItem.itemCount = 1.0;
        this.newShoppingCartItem.inHouseService = null;
        this.newShoppingCartItem.alternativeLabel = null;
        this.newShoppingCartItem.date = null;
      } else {
        this.newShoppingCartItem.itemPrice = obj.amount;
        this.newShoppingCartItem.itemCount = 1.0;
        this.newShoppingCartItem.inHouseService = obj;
        this.newShoppingCartItem.alternativeLabel = null;
        this.newShoppingCartItem.date = obj.rateMode === 'PerRoom' && obj.rateFrequency === 'PerStay' ? (this.newShoppingCartItem.date || this.maxCheckOutDate) : null;
      }
    },
    addShoppingCartItem: function () {
      const offer = this.selectedOffers.find(it => this.showAddTransactionModal === it.offerId);

      if (isNil(this.newShoppingCartItem.inHouseServiceId)) {
        return;
      }
      if (isNil(this.newShoppingCartItem.itemCount === 0)) {
        return;
      }
      if (!isNil(offer)) {
        if (isNil(offer.shoppingCart)) {
          offer.shoppingCart = [this.newShoppingCartItem];
        } else {
          if (offer.shoppingCart.filter(it => it.inHouseServiceId === this.newShoppingCartItem.inHouseServiceId).length > 0) {
            // this.showWarningMessage('Diese Zusatzleistung wurde zuvor bereits mind. 1 mal hinzugefügt.');
          }
          offer.shoppingCart.push(this.newShoppingCartItem);
        }

        this.debouncedUpdateRates();

        this.newShoppingCartItem = {
          id: uuid.v4(),
          inHouseServiceId: null,
          itemPrice: 0.0,
          itemCount: 1.0,
          alternativeLabel: null,
          date: null,
        };
      }

      this.showAddTransactionModal = false;
    },
    isEmpty,
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

</style>

<style lang="scss">
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/pages/PageDraftReservation.vue